import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { fieldRequired, maxCharacterLength } from '../../util/constants.js';

/**
 * Reusable form shared by Feedback and Contact Us.
 * Manages its own select/description/error state and calls onSubmit with { type, description }.
 */
export default function FeedbackContactForm({
  title,
  selectLabel,
  selectOptions,
  descriptionLabel,
  maxDescLength,
  submitLabel,
  onSubmit,
  onClose,
}) {
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState({ type: null, desc: null });

  const validate = () => {
    const errors = {};

    if (!type) {
      errors.type = fieldRequired(selectLabel);
    }

    if (!description.trim()) {
      errors.desc = fieldRequired(descriptionLabel);
    }

    setError(errors);
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (errors.type || errors.desc) return;
    onSubmit({ type, description });
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setType(value);
    if (value) setError((prev) => ({ ...prev, type: null }));
  };

  const handleDescChange = (e) => {
    const value = e.target.value;
    setDescription(value);

    if (value.length > maxDescLength) {
      setError((prev) => ({
        ...prev,
        desc: maxCharacterLength(descriptionLabel, maxDescLength),
      }));
    } else {
      setError((prev) => ({ ...prev, desc: null }));
    }
  };

  const handleDescFocus = () => {
    if (!type) {
      setError((prev) => ({ ...prev, type: fieldRequired(selectLabel) }));
    }
  };

  return (
    <Box className="col gap-lg" component="form" onSubmit={handleSubmit}>
      <div className="row justify-between align-center">
        <Typography variant="h6">{title}</Typography>
        {onClose && (
          <Tooltip title="Close" arrow placement="top">
            <IconButton color="tertiary" onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <TextField
        select
        fullWidth
        label={selectLabel}
        value={type}
        onChange={handleTypeChange}
        error={!!error.type}
        helperText={error.type}
        SelectProps={{
          MenuProps: {
            disablePortal: true,
          },
        }}
      >
        {selectOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        multiline
        rows={6}
        fullWidth
        label={`${descriptionLabel} (${description.length}/${maxDescLength} characters)`}
        value={description}
        onChange={handleDescChange}
        onFocus={handleDescFocus}
        error={!!error.desc}
        helperText={error.desc}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disableElevation
        disabled={!type || !description.trim() || description.length > maxDescLength}
        style={{ marginLeft: 'auto' }}
      >
        {submitLabel}
      </Button>
    </Box>
  );
}

FeedbackContactForm.defaultProps = {
  descriptionLabel: 'Description',
  maxDescLength: 2000,
  submitLabel: 'Submit',
  onClose: undefined,
};

FeedbackContactForm.propTypes = {
  title: PropTypes.string.isRequired,
  selectLabel: PropTypes.string.isRequired,
  selectOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  descriptionLabel: PropTypes.string,
  maxDescLength: PropTypes.number,
  submitLabel: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};
